import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { directAxiosInstance } from "@lib/api"
import { getAssetPath, Validate } from "@commonUtils"
import InputField from "@components/nonDLSComponents/InputField/InputField.js"
import { updateAuthenticationInfo, setDefault, setAuthType, clearUserInfo } from "../reducer"
import { setDefaultAuthConfig } from "@containers/App/reducer"
import { onGetVerificationCodeGA } from "../GAEvents"
import Button from "@components/Button/Button"
import css from "../Auth.scss"
import CheckBox from "@components/nonDLSComponents/CheckBox/CheckBox.js"
import { useDispatch, useSelector } from "react-redux"
import ARIAButton from "@wrappers/ARIAButton/ARIAButton"

const SignUp = ({ forwardHandler, state, localDispatch }) => {
    const dispatch = useDispatch()
    const { info } = state
    const { authConfig } = useSelector((state) => state.shellReducer)
    const [error, setError] = useState(null)
    const [ctaLabel, setCtaLabel] = useState("Continue")
    const [phone, setPhone] = useState(info.phone || "")
    const [isDoctor, setIsDoctor] = useState(false)
    // we are using "active" as a flag so that untill the request is completed, we do not allow multiple request
    // on multiple clicks on cta
    const [active, setActive] = useState(false)
    const [iconUrl, setIconUrl] = useState(null)

    const inputRef = useRef(null)
    const setInputFocus = () => {
        setFocused(true)
        inputRef.current.focus()
    }

    /**
     * @description when user come on this screen cursor should be on inputField .
     */
    useEffect(() => {
        setInputFocus()
    }, [])

    useEffect(() => {
        if (!error) return

        if (error.phone) {
            setInputFocus()
        }
    }, [error])

    const [focused, setFocused] = useState(false)
    const onFocus = () => {
        setFocused(true)
    }
    /**
     * @description Function to check valid input
     */
    const errorCheck = () => {
        setError(null)
        if (phone === "") {
            setFocused(false)
        }
        const errs = Validate({
            phone: phone,
        })
        setError(errs)

        return errs
    }

    const onBlur = () => {
        if (phone === "") {
            setFocused(false)
        }
    }

    /**
     *
     * @description Function to submit the form by clicking on enter
     */
    const onKeyUp = (event) => {
        if (event.key === "Enter") {
            handleSubmit()
        }
    }
    /**
     * @description Function to create otp request for phone number flow
     */
    const handleSubmit = () => {
        const errs = errorCheck()
        if (!errs && !active) {
            localDispatch(clearUserInfo())
            setActive(true)
            setIconUrl(getAssetPath("svgs/icons/loading-wheel_24px_background.svg"))
            directAxiosInstance()
                .post("auth/create_token", {
                    number: phone,
                    is_doctor: isDoctor,
                })
                .then((res) => {
                    if (res && res.data) {
                        if (!res.data.errors) {
                            const { is_success } = res
                            const userFlags = res?.data?.user_flags
                            const existingUser = userFlags?.user_type === "existing"
                            localDispatch(updateAuthenticationInfo({ existingUser: existingUser }))
                            if (is_success) {
                                if (phone) localDispatch(updateAuthenticationInfo({ phone: phone }))
                                onGetVerificationCodeGA("Success")
                                forwardHandler("SignInOtp")
                            }
                        }
                        setActive(false)
                        setIconUrl(null)
                        setCtaLabel("Continue")
                    }
                })
                .catch((err) => {
                    let errMsg = err?.data?.error?.message || "Server error"
                    if (err?.data?.error?.errors[0]?.message) {
                        errMsg = err.data.error.errors[0].message
                    }
                    setActive(false)
                    setError({ phone: errMsg })
                    setIconUrl(null)
                    setCtaLabel("Continue")
                    onGetVerificationCodeGA("Error")
                })
        }
    }

    return (
        <>
            <div className={css.rightContainer}>
                {!authConfig.restrictUserFromClosingAuth && (
                    <div className={`flex ${css.crossIcon} ${css.cursorPointer}`}>
                        <div>
                            <ARIAButton
                                onClick={() => {
                                    dispatch(setDefaultAuthConfig())
                                    localDispatch(setDefault())
                                }}
                                ariaLabel={"Close login popup"}
                            >
                                <img
                                    src={getAssetPath("svgs/icons/cross_18px_primary.svg")}
                                    alt={"Close icon"}
                                />
                            </ARIAButton>
                        </div>
                    </div>
                )}
                <div className={css.authContainer}>
                    <div className={`${css.paddingAuth} flex flexColumn justifyBetween`}>
                        <div>
                            <span className={css.heading}>Sign Up</span>
                            <div className={`smallRegular textSecondary ${css.subHeadingMargin}`}>
                                Please enter your Mobile number to receive One Time Password (OTP)
                            </div>

                            <div className={css.marginBottom16}>
                                <InputField
                                    name="phone"
                                    label="Enter 10 digit mobile number"
                                    type="tel"
                                    prefixText="+91"
                                    value={phone}
                                    onChange={setPhone}
                                    onBlur={onBlur}
                                    alertType={"error"}
                                    onFocus={() => onFocus()}
                                    alertMessage={error && error.phone}
                                    focused={focused}
                                    borderType={"primary"}
                                    onKeyUp={(e) => onKeyUp(e)}
                                    ref={inputRef}
                                    required
                                />
                            </div>
                            <div className={css.marginTop20}>
                                <CheckBox
                                    alignCenter
                                    onClick={() => setIsDoctor(!isDoctor)}
                                    isDisabled={false}
                                    isSelected={isDoctor}
                                    child={
                                        <span className={`xSmallRegular textSecondary ${css.marginLeft10}`}>
                                            Are You a HealthCare Expert?
                                        </span>
                                    }
                                />
                            </div>
                        </div>
                        <div className=" flexColumn alignCenter justifyCenter">
                            <Button
                                label={!iconUrl ? ctaLabel : null}
                                iconUrl={iconUrl ? iconUrl : null}
                                isDisabled={iconUrl ? true : false}
                                onClick={() => handleSubmit()}
                            />
                            <div className="marginTop-16 flexColumn alignCenter">
                                <span className="marginTop-8">
                                    Already on 1mg ?
                                    <span
                                        className={`textAction bodyMedium ${css.marginLeft4} ${css.cursorPointer}`}
                                        onClick={() => {
                                            localDispatch(clearUserInfo())
                                            localDispatch(setAuthType("Login"))
                                        }}
                                    >
                                        <span>Login</span>
                                    </span>
                                </span>
                                <div className={css.footerSubText}>
                                    <span className="flexColumn alignCenter textTertiary">
                                        By signing up, you agree to our{" "}
                                        <div>
                                            <span
                                                className={`textTertiary ${css.underlineText}`}
                                                onClick={() => {
                                                    dispatch(setDefaultAuthConfig())
                                                    window.location.href = "/tnc"
                                                }}
                                            >
                                                Terms and Conditions{" "}
                                            </span>
                                            <span className={css.marginBoth4}>&</span>{" "}
                                            <span
                                                className={`textTertiary ${css.underlineText}`}
                                                onClick={() => {
                                                    dispatch(setDefaultAuthConfig())
                                                    window.location.href = "/PrivacyPolicy"
                                                }}
                                            >
                                                Privacy policy{" "}
                                            </span>
                                        </div>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

SignUp.propTypes = {
    forwardHandler: PropTypes.func.isRequired,
    state: PropTypes.shape({
        info: PropTypes.shape({
            phone: PropTypes.string,
        }),
    }).isRequired,
    localDispatch: PropTypes.func.isRequired,
}

export default SignUp
